import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useRouter } from 'next/router';
import { useTheme } from '../context/ThemeContext';
import DocsResourcesView from './DocsResourcesView';
import TermsPrivacyView from './TermsPrivacyView';
import MyThemeView from './MyThemeView';

export default function MyAccountView({ onClose, username }) {
  const router = useRouter();
  const { logout } = useAuth();
  const [activeSection, setActiveSection] = useState(null);
  const [openPopover, setOpenPopover] = useState(null);
  const [showDocsResources, setShowDocsResources] = useState(false);
  const [showTermsPrivacy, setShowTermsPrivacy] = useState(false);
  const [showThemeView, setShowThemeView] = useState(false);
  const docsMenuItemRef = useRef(null);
  const termsMenuItemRef = useRef(null);
  const themeMenuItemRef = useRef(null);
  const { theme, setTheme } = useTheme();
  
  // Mock data - replace with actual API data
  const accountData = {
    totalCredits: 250000,
    remainingCredits: 120000, 
    apiKeys: ['hcl-api-123456789', 'hcl-api-987654321']
  };
  
  // Menu items for the account view
  const menuItems = [
    {
      title: 'Credits',
      items: [
        {
          label: 'Total Credits',
          value: accountData.totalCredits.toLocaleString(),
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
            </svg>
          ),
        },
        {
          label: 'Remaining Credits',
          value: accountData.remainingCredits.toLocaleString(),
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
          ),
        },
      ],
    },
    {
      title: 'Account',
      items: [
        {
          label: 'Profile',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          ),
          action: () => setActiveSection('profile'),
        },
        {
          label: 'API Keys',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          ),
          action: () => setActiveSection('apiKeys'),
        },
        {
          label: 'Subscription',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
            </svg>
          ),
          action: () => setActiveSection('subscription'),
        },
      ],
    },
    {
      title: 'Preferences',
      items: [
        {
          label: 'Pronunciation Dictionaries',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
            </svg>
          ),
          action: () => setActiveSection('dictionaries'),
        },
        {
          label: 'Theme',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
            </svg>
          ),
          action: () => setShowThemeView(true),
        },
        {
          label: 'Usage Analytics',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
          ),
          action: () => setActiveSection('analytics'),
        },
      ],
    },
    {
      title: 'Support',
      items: [
        {
          label: 'Docs & Resources',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
            </svg>
          ),
          action: () => setActiveSection('docs'),
        },
        {
          label: 'Terms & Privacy',
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
            </svg>
          ),
          action: () => setActiveSection('terms'),
        },
      ],
    },
  ];

  // Close when clicking outside
  const viewRef = useRef(null);
  
  // Handle focus and hover events for Docs & Resources menu item
  useEffect(() => {
    if (!docsMenuItemRef.current) return;
    
    const handleFocus = () => {
      setShowDocsResources(true);
      // Close any other popovers
      setOpenPopover(null);
      setShowTermsPrivacy(false);
    };
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowDocsResources(true);
      }
    };
    
    let leaveTimer;
    const handleMouseLeave = () => {
      // Delay closing a bit to allow moving mouse to the DocsResourcesView
      leaveTimer = setTimeout(() => {
        const docsResourcesView = document.querySelector('[data-docs-resources-view]');
        if (!docsResourcesView?.matches(':hover')) {
          setShowDocsResources(false);
        }
      }, 100);
    };
    
    const docsMenuItem = docsMenuItemRef.current;
    docsMenuItem.addEventListener('focus', handleFocus);
    docsMenuItem.addEventListener('mouseenter', handleFocus);
    docsMenuItem.addEventListener('mouseleave', handleMouseLeave);
    docsMenuItem.addEventListener('keydown', handleKeyDown);
    
    return () => {
      clearTimeout(leaveTimer);
      docsMenuItem.removeEventListener('focus', handleFocus);
      docsMenuItem.removeEventListener('mouseenter', handleFocus);
      docsMenuItem.removeEventListener('mouseleave', handleMouseLeave);
      docsMenuItem.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  // Handle focus and hover events for Terms & Privacy menu item
  useEffect(() => {
    if (!termsMenuItemRef.current) return;
    
    const handleFocus = () => {
      setShowTermsPrivacy(true);
      // Close any other popovers
      setOpenPopover(null);
      setShowDocsResources(false);
    };
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowTermsPrivacy(true);
      }
    };
    
    let leaveTimer;
    const handleMouseLeave = () => {
      // Delay closing a bit to allow moving mouse to the TermsPrivacyView
      leaveTimer = setTimeout(() => {
        const termsPrivacyView = document.querySelector('[data-terms-privacy-view]');
        if (!termsPrivacyView?.matches(':hover')) {
          setShowTermsPrivacy(false);
        }
      }, 100);
    };
    
    const termsMenuItem = termsMenuItemRef.current;
    termsMenuItem.addEventListener('focus', handleFocus);
    termsMenuItem.addEventListener('mouseenter', handleFocus);
    termsMenuItem.addEventListener('mouseleave', handleMouseLeave);
    termsMenuItem.addEventListener('keydown', handleKeyDown);
    
    return () => {
      clearTimeout(leaveTimer);
      termsMenuItem.removeEventListener('focus', handleFocus);
      termsMenuItem.removeEventListener('mouseenter', handleFocus);
      termsMenuItem.removeEventListener('mouseleave', handleMouseLeave);
      termsMenuItem.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  // Handle focus and hover events for Theme menu item
  useEffect(() => {
    if (!themeMenuItemRef.current) return;
    
    const handleFocus = () => {
      setShowThemeView(true);
      // Close any other popovers
      setOpenPopover(null);
      setShowDocsResources(false);
      setShowTermsPrivacy(false);
    };
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowThemeView(true);
      }
    };
    
    let leaveTimer;
    const handleMouseLeave = () => {
      // Delay closing a bit to allow moving mouse to the ThemeView
      leaveTimer = setTimeout(() => {
        const themeView = document.querySelector('[data-theme-view]');
        if (!themeView?.matches(':hover')) {
          setShowThemeView(false);
        }
      }, 100);
    };
    
    const themeMenuItem = themeMenuItemRef.current;
    themeMenuItem.addEventListener('focus', handleFocus);
    themeMenuItem.addEventListener('mouseenter', handleFocus);
    themeMenuItem.addEventListener('mouseleave', handleMouseLeave);
    themeMenuItem.addEventListener('keydown', handleKeyDown);
    
    return () => {
      clearTimeout(leaveTimer);
      themeMenuItem.removeEventListener('focus', handleFocus);
      themeMenuItem.removeEventListener('mouseenter', handleFocus);
      themeMenuItem.removeEventListener('mouseleave', handleMouseLeave);
      themeMenuItem.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the click is outside of the accountView and not on the account button
      // and not on any of the popovers
      const isDocsResourcesViewClick = event.target.closest('[data-docs-resources-view]');
      const isDocsResourcesTriggerClick = event.target.closest('[data-docs-resources-trigger]');
      const isTermsPrivacyViewClick = event.target.closest('[data-terms-privacy-view]');
      const isTermsPrivacyTriggerClick = event.target.closest('[data-terms-privacy-trigger]');
      const isThemeViewClick = event.target.closest('[data-theme-view]');
      const isThemeTriggerClick = event.target.closest('[data-theme-trigger]');
      
      // Close account view if clicking outside it and not on any popover
      if (viewRef.current && !viewRef.current.contains(event.target) && 
          !event.target.closest('[data-account-button="true"]') &&
          !isDocsResourcesViewClick &&
          !isTermsPrivacyViewClick &&
          !isThemeViewClick) {
        onClose();
      }
      
      // Close popovers when clicking outside them
      if (openPopover && viewRef.current && !event.target.closest('[data-popover]')) {
        setOpenPopover(null);
      }
      
      // Don't close DocsResourcesView when clicking inside it or on its trigger
      if (showDocsResources && !isDocsResourcesViewClick && !isDocsResourcesTriggerClick) {
        setShowDocsResources(false);
      }
      
      // Don't close TermsPrivacyView when clicking inside it or on its trigger
      if (showTermsPrivacy && !isTermsPrivacyViewClick && !isTermsPrivacyTriggerClick) {
        setShowTermsPrivacy(false);
      }
      
      // Don't close ThemeView when clicking inside it or on its trigger
      if (showThemeView && !isThemeViewClick && !isThemeTriggerClick) {
        setShowThemeView(false);
      }
    }
    
    // Use capture phase to ensure we get the event first
    document.addEventListener("mousedown", handleClickOutside, true);
    
    // Add escape key handler
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showDocsResources) {
          // Close DocsResourcesView first if it's open
          event.preventDefault(); // Prevent the event from bubbling
          event.stopPropagation();
          setShowDocsResources(false);
        } else if (showTermsPrivacy) {
          // Close TermsPrivacyView next if it's open
          event.preventDefault();
          event.stopPropagation();
          setShowTermsPrivacy(false);
        } else if (showThemeView) {
          // Close ThemeView if it's open
          event.preventDefault();
          event.stopPropagation();
          setShowThemeView(false);
        } else if (openPopover) {
          // Close popover next if one is open
          setOpenPopover(null);
        } else {
          // Otherwise close the account view
          onClose();
        }
      }
    };
    
    document.addEventListener("keydown", handleEscKey);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onClose, openPopover, showDocsResources, showTermsPrivacy]);

  return (
    <div 
      ref={viewRef}
      className="fixed inset-x-0 bottom-0 mx-auto md:left-64 md:bottom-20 md:inset-x-auto w-full md:w-72 bg-white dark:bg-dark-card rounded-t-xl md:rounded-md shadow-xl border-t md:border border-gray-200 dark:border-dark-border overflow-hidden flex flex-col z-20 max-h-[85vh] md:max-h-none modal-container"
    >
      {/* Mobile Handle */}
      <div className="md:hidden bottom-sheet-handle"></div>
      
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">My Account</h3>
        <button
          onClick={onClose}
          className="p-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-target"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto max-h-[400px] md:max-h-[400px] modal-content smooth-scroll">
        {menuItems.map((section, index) => (                <div key={index} className="py-2 px-3 border-b dark:border-dark-border last:border-b-0">
            <div className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400 mb-1.5">{section.title}</div>
            <div className="space-y-0.5">
              {section.items.map((item, itemIndex) => {
                // Check if this is one of the items that should show a disclosure button
                const showDisclosure = ['Theme', 'Docs & Resources', 'Terms & Privacy'].includes(item.label);
                
                const handleItemClick = () => {
                  // Special handling for Docs & Resources
                  if (item.label === 'Docs & Resources') {
                    setShowDocsResources(true); // Always show on click
                    // Close other popovers
                    setOpenPopover(null);
                    setShowTermsPrivacy(false);
                    return;
                  }
                  
                  // Special handling for Terms & Privacy
                  if (item.label === 'Terms & Privacy') {
                    setShowTermsPrivacy(true); // Always show on click
                    // Close other popovers
                    setOpenPopover(null);
                    setShowDocsResources(false);
                    return;
                  }

                  // For other items with disclosure, toggle popover instead of navigation
                  if (showDisclosure) {
                    setOpenPopover(openPopover === item.label ? null : item.label);
                    // Close docs resources view
                    setShowDocsResources(false);
                  } else if (item.action) {
                    // For navigation items, handle route change
                    switch(item.label) {
                      case 'Profile':
                        router.push('/profile');
                        onClose();
                        break;
                      case 'API Keys':
                        router.push('/api-keys');
                        onClose();
                        break;
                      case 'Subscription':
                        router.push('/subscription');
                        onClose();
                        break;
                      case 'Pronunciation Dictionaries':
                        router.push('/dictionaries');
                        onClose();
                        break;
                      case 'Usage Analytics':
                        router.push('/analytics');
                        onClose();
                        break;
                      default:
                        if (item.action) item.action();
                        break;
                    }
                  }
                };
                
                return (
                  <div key={itemIndex} className="relative">
                    <div 
                      ref={item.label === 'Docs & Resources' 
                          ? docsMenuItemRef 
                          : item.label === 'Terms & Privacy' 
                            ? termsMenuItemRef
                            : item.label === 'Theme'
                              ? themeMenuItemRef
                              : null}
                      onClick={handleItemClick}
                      tabIndex={['Docs & Resources', 'Terms & Privacy', 'Theme'].includes(item.label) ? 0 : undefined}
                      role={['Docs & Resources', 'Terms & Privacy', 'Theme'].includes(item.label) ? 'button' : undefined}
                      onMouseEnter={
                        item.label === 'Docs & Resources' 
                          ? () => setShowDocsResources(true) 
                          : item.label === 'Terms & Privacy' 
                            ? () => setShowTermsPrivacy(true) 
                            : item.label === 'Theme'
                              ? () => setShowThemeView(true)
                              : undefined
                      }
                      className={`flex items-center justify-between py-1.5 px-2 rounded cursor-pointer transition-colors duration-150 ${
                        (item.label === 'Docs & Resources' && showDocsResources) || (item.label === 'Terms & Privacy' && showTermsPrivacy) || (item.label === 'Theme' && showThemeView)
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
                          : 'hover:bg-blue-50 dark:hover:bg-gray-800'
                      }`}
                      data-docs-resources-trigger={item.label === 'Docs & Resources' ? "true" : undefined}
                      data-terms-privacy-trigger={item.label === 'Terms & Privacy' ? "true" : undefined}
                      data-theme-trigger={item.label === 'Theme' ? "true" : undefined}
                      aria-expanded={
                        item.label === 'Docs & Resources' 
                          ? showDocsResources 
                          : item.label === 'Terms & Privacy' 
                            ? showTermsPrivacy 
                            : undefined
                      }
                    >
                      <div className="flex items-center">
                        <div className="text-gray-500 dark:text-gray-400 mr-2 group-hover:text-blue-600 dark:group-hover:text-blue-400">{item.icon}</div>
                        <div className="text-xs text-gray-800 dark:text-gray-200">{item.label}</div>
                      </div>
                      {item.value && <div className="text-xs text-gray-600 dark:text-gray-400">{item.value}</div>}
                      {showDisclosure && (
                        <div className="text-gray-400 dark:text-gray-500">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    {/* Disclosure popover for Theme */}
                    {openPopover === 'Theme' && item.label === 'Theme' && (
                      <div data-popover className="absolute left-full top-0 ml-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-30">
                        <div className="p-2">
                          <div className="text-xs font-semibold mb-2 text-gray-700 dark:text-gray-300">Select Theme</div>
                          <div 
                            className={`flex items-center p-1.5 hover:bg-blue-50 dark:hover:bg-gray-700 rounded cursor-pointer ${theme === 'light' ? 'bg-blue-50 dark:bg-gray-700' : ''}`}
                            onClick={() => {
                              // We already have access to the theme context at the top level
                              setTheme('light');
                              setOpenPopover(null);
                            }}
                          >
                            <div className="w-4 h-4 bg-blue-600 rounded-full mr-2"></div>
                            <div className="text-xs text-gray-800 dark:text-gray-200">Light (Default)</div>
                          </div>
                          <div 
                            className={`flex items-center p-1.5 hover:bg-blue-50 dark:hover:bg-gray-700 rounded cursor-pointer ${theme === 'dark' ? 'bg-blue-50 dark:bg-gray-700' : ''}`}
                            onClick={() => {
                              // We already have access to the theme context at the top level
                              setTheme('dark');
                              setOpenPopover(null);
                            }}
                          >
                            <div className="w-4 h-4 bg-gray-800 rounded-full mr-2"></div>
                            <div className="text-xs text-gray-800 dark:text-gray-200">Dark</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Show nothing here - we're using DocsResourcesView component instead */}
                    
                    {/* TermsPrivacyView is rendered separately at the bottom of component */}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer - Sign out button */}
      <div className="bg-gray-50 dark:bg-dark-card py-2 px-3 border-t dark:border-dark-border sticky bottom-0">
        <button 
          onClick={logout}
          className="w-full flex items-center justify-center space-x-1 py-1.5 px-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
          </svg>
          <span className="text-xs">Sign Out</span>
        </button>
      </div>
      
      {/* Docs & Resources View - Rendered in a portal for proper positioning */}
      {showDocsResources && (
        <div className="fixed inset-0 z-40 pointer-events-none">
          <div className="pointer-events-auto">
            <DocsResourcesView 
              onClose={() => setShowDocsResources(false)} 
              onNavigate={onClose} // This will close the MyAccountView when navigation happens
            />
          </div>
        </div>
      )}
      
      {/* Terms & Privacy View - Rendered in a portal for proper positioning */}
      {showTermsPrivacy && (
        <div className="fixed inset-0 z-40 pointer-events-none">
          <div className="pointer-events-auto">
            <TermsPrivacyView 
              onClose={() => setShowTermsPrivacy(false)} 
              onNavigate={onClose} // This will close the MyAccountView when navigation happens
            />
          </div>
        </div>
      )}
      
      {/* Theme View - Rendered in a portal for proper positioning */}
      {showThemeView && (
        <div className="fixed inset-0 z-40 pointer-events-none">
          <div className="pointer-events-auto">
            <MyThemeView 
              onClose={() => setShowThemeView(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
